import WorkflowSchema from '../models/workflow-schema.js';
import { JobInputValues } from '../models/job.js';
import { requiredInputKeys } from '../ns-workflows/workflow-entry.js';
import { invitePrompts, launchPicks } from '../views/workflow-studio/settings.js';
import type { Permission } from '../helpers/permissions.js';

const COMPANY_CONTEXT_INPUTS = ['BASE_COMPANY_ID', 'COMPANY_ID'];
// launchWorkflow() fills the active company and the launching person itself
const LAUNCH_CONTEXT_INPUTS = [...COMPANY_CONTEXT_INPUTS, 'ASSIGNED_TO'];
const DEVELOPER_WORKFLOWS = ['@ns/lambda-error-debug'];

// They open relationships with other companies, which takes RELATIONSHIPS_MANAGE (D7, D23)
const RELATIONSHIP_WORKFLOWS = ['@ns/company-connect-request', '@ns/enterprise-onboarding'];
// Their cards invite colleagues, which takes PEOPLE_INVITE once onboarding is over
const PEOPLE_INVITE_WORKFLOWS = ['@ns/invite-company-employees', '@ns/onboard-company-employees'];

// The permission a launcher must hold, as workflow/create or the workflow's first card would refuse anyone else
export function launchPermission(schema: Pick<WorkflowSchema, 'name' | 'nodes'>): Permission | null {
  if (RELATIONSHIP_WORKFLOWS.includes(schema.name) || (schema.nodes ?? []).some((node) => node.type === 'invite')) {
    return 'RELATIONSHIPS_MANAGE';
  }
  return PEOPLE_INVITE_WORKFLOWS.includes(schema.name) ? 'PEOPLE_INVITE' : null;
}

// workflow/create refuses an entry the schema doesn't open to the app; lambdas and parent workflows start the rest
export function appCanLaunch(schema: WorkflowSchema): boolean {
  return Boolean(schema.entry?.launcher?.launchedBy?.includes('client'));
}

// Workflows needing anything beyond the active company (a relationship, an invitation) are started from their own screens.
export function isLaunchable(schema: WorkflowSchema, devTools: boolean): boolean {
  if (!appCanLaunch(schema)) return false;
  const launcher = schema.entry.launcher;
  if (DEVELOPER_WORKFLOWS.includes(schema.name)) return devTools;
  const picked = [
    ...launchPicks(schema).map((pick) => pick.key),
    ...invitePrompts(schema).flatMap((prompt) => Object.values(prompt.inputs)),
  ];
  return requiredInputKeys(launcher.inputs).every((key) => LAUNCH_CONTEXT_INPUTS.includes(key) || picked.includes(key));
}

// A company workflow's launch page, for one that asks its launcher to pick something or name an invitee first
export function launchPagePath(schema: WorkflowSchema): string | null {
  const [, , companyWorkflowId] = schema.name.split('/');
  return schema.kind === 'COMPANY' &&
    companyWorkflowId &&
    (launchPicks(schema).length > 0 || invitePrompts(schema).length > 0)
    ? `/workflow-studio/${companyWorkflowId}/start`
    : null;
}

export function launcherInputs(schema: WorkflowSchema, companyId: string): JobInputValues {
  return Object.fromEntries(
    requiredInputKeys(schema.entry.launcher.inputs)
      .filter((key) => COMPANY_CONTEXT_INPUTS.includes(key))
      .map((key) => [key, companyId]),
  );
}
